import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Building2, CheckCircle2, Sparkles, FolderPlus, UserPlus, GraduationCap, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LangContext';
import { toast } from 'sonner';
import { trackEvent } from '@/lib/analytics';
import { log } from '@/lib/log';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
    'Énergie', 'Santé', 'Agriculture', 'Infrastructure', 'Services',
    'Mines', 'ONG / Associatif', 'Institution publique', 'Autre',
];

const STEPS = [
    { key: 'workspace_done', icon: <Building2 size={20} />, title: "Votre espace projet", subtitle: "Quelques informations sur votre organisation" },
    { key: 'project_done', icon: <FolderPlus size={20} />, title: "Premier projet", subtitle: "Un modèle adapté à votre secteur, prêt à personnaliser" },
    { key: 'invite_done', icon: <UserPlus size={20} />, title: "Inviter votre équipe", subtitle: "Collaborez dès le premier jour" },
    { key: 'campus_done', icon: <GraduationCap size={20} />, title: "Campus 360°", subtitle: "L'académie panafricaine du pilotage projet" },
];

export default function Onboarding() {
    const { t } = useLang();
    const { workspaces, currentWorkspace, createWorkspace, refresh, loading: wsLoading } = useWorkspace();
    const { session } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    // Step 1 form
    const [name, setName] = useState('');
    const [country, setCountry] = useState('');
    const [industry, setIndustry] = useState(INDUSTRIES[0]);
    const [error, setError] = useState(null);

    // Step 2 result
    const [seededProject, setSeededProject] = useState(null);

    // Step 3 form
    const [inviteEmail, setInviteEmail] = useState('');

    const ws = currentWorkspace;
    const status = ws?.onboarding_status || {};

    // Auto-jump to next pending step on mount when reopening (e.g. user comes back later)
    useEffect(() => {
        if (wsLoading || !ws) return;
        if (status.workspace_done && !status.project_done) setStep(1);
        else if (status.project_done && !status.invite_done) setStep(2);
        else if (status.invite_done && !status.campus_done) setStep(3);
        // else stay on step 0 to allow review
    }, [wsLoading, ws?.id]); // eslint-disable-line

    const authHeader = () => ({ headers: { Authorization: `Bearer ${session?.access_token}` } });

    // ── Step 1 : Create workspace ──
    const submitStep1 = async (e) => {
        e?.preventDefault?.();
        if (!name.trim()) { setError("Le nom de l'organisation est requis"); return; }
        setSubmitting(true); setError(null);
        const { error: err } = await createWorkspace({ name, country, industry });
        setSubmitting(false);
        if (err) { setError(err.message); return; }
        // Mark step (best-effort) — workspace_done is auto on creation actually
        toast.success(`Espace ${name} créé ✓`);
        trackEvent('workspace_created', { industry, country });
        await refresh();
        setStep(1);
    };

    // ── Step 2 : Seed sector-specific project ──
    const seedProject = async () => {
        if (!ws?.id) return;
        setSubmitting(true);
        try {
            const r = await axios.post(`${API}/workspaces/${ws.id}/onboarding/seed-project`,
                { workspace_id: ws.id, sector_label: ws.industry || industry },
                authHeader());
            setSeededProject({ id: r.data.project_id, name: r.data.project_name || 'Premier projet' });
            toast.success("Projet modèle créé ✓");
            trackEvent('project_seeded', { industry: ws.industry || industry });
            await refresh();
            setStep(2);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Création échouée');
        } finally {
            setSubmitting(false);
        }
    };

    const skipStep2 = async () => {
        if (!ws?.id) return setStep(2);
        try {
            await axios.patch(`${API}/workspaces/${ws.id}/onboarding/step`,
                { step: 'project_done', done: true }, authHeader());
            await refresh();
        } catch (e) {
            // Onboarding skip is intentionally tolerant: failure on the optional
            // step-tracking endpoint must not block the user's progression.
            log.warn('[onboarding] skip step2 telemetry failed:', e?.response?.status || e?.message);
        }
        setStep(2);
    };

    // ── Step 3 : Invite teammate ──
    const sendInvite = async () => {
        if (!inviteEmail.includes('@') || !ws?.id) {
            toast.error('Email invalide'); return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/invitations/send`,
                { workspace_id: ws.id, email: inviteEmail.trim(), role: 'editor', is_admin: false }, authHeader());
            toast.success(`Invitation envoyée à ${inviteEmail}`);
            trackEvent('invite_sent');
            await axios.patch(`${API}/workspaces/${ws.id}/onboarding/step`,
                { step: 'invite_done', done: true }, authHeader());
            await refresh();
            setStep(3);
        } catch (e) {
            // Skip-friendly : even if invite endpoint differs, allow progression
            toast.error(e?.response?.data?.detail || 'Envoi échoué (vous pourrez réessayer plus tard)');
        } finally {
            setSubmitting(false);
        }
    };

    const skipStep3 = async () => {
        if (!ws?.id) return setStep(3);
        try {
            await axios.patch(`${API}/workspaces/${ws.id}/onboarding/step`,
                { step: 'invite_done', done: true }, authHeader());
            await refresh();
        } catch (e) {
            log.warn('[onboarding] skip step3 telemetry failed:', e?.response?.status || e?.message);
        }
        setStep(3);
    };

    // ── Step 4 : Finish ──
    const finish = async () => {
        if (!ws?.id) {
            navigate('/app/dashboard'); return;
        }
        try {
            await axios.patch(`${API}/workspaces/${ws.id}/onboarding/step`,
                { step: 'campus_done', done: true }, authHeader());
            await refresh();
        } catch (e) {
            log.warn('[onboarding] finish telemetry failed:', e?.response?.status || e?.message);
        }
        navigate('/app/dashboard');
    };

    if (wsLoading) {
        return <div className="py-20 text-center"><Loader2 className="animate-spin inline mr-2" size={16} /> Chargement…</div>;
    }

    // Step 1 needs no workspace yet (we're about to create one)
    // Steps 2-4 require currentWorkspace
    const progressPct = Math.round(((step + 1) / STEPS.length) * 100);

    return (
        <div className="max-w-3xl mx-auto" data-testid="onboarding-wizard">
            {/* Header + Progress */}
            <div className="flex items-center gap-4 mb-6">
                <div className="h-12 w-12 rounded-xl bg-primary-light text-primary flex items-center justify-center shrink-0">
                    {STEPS[step].icon}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="eyebrow">Étape {step + 1} / {STEPS.length}</div>
                    <h1 className="font-heading text-3xl truncate">{STEPS[step].title}</h1>
                </div>
                <div className="text-right text-sm">
                    <div className="text-xs text-foreground/55 mb-1">{progressPct}%</div>
                    <div className="w-24 h-1.5 rounded-full bg-surface-muted overflow-hidden">
                        <div className="h-full bg-primary transition-all duration-500" style={{ width: `${progressPct}%` }} data-testid="onboarding-progress-bar" />
                    </div>
                </div>
            </div>
            <p className="text-foreground/65 mb-8 text-sm">{STEPS[step].subtitle}</p>

            {/* Step indicators */}
            <div className="flex items-center gap-2 mb-8" data-testid="onboarding-stepper">
                {STEPS.map((s, i) => {
                    const done = status[s.key] || (i < step);
                    const active = i === step;
                    return (
                        <div key={s.key} className={`flex-1 h-1 rounded-full transition-colors ${active ? 'bg-primary' : done ? 'bg-primary/60' : 'bg-surface-muted'}`} />
                    );
                })}
            </div>

            {/* ─── STEP 0 : Create workspace ─── */}
            {step === 0 && !ws && (
                <form onSubmit={submitStep1} className="space-y-6 card-soft p-8" data-testid="onboarding-form-step0">
                    <div className="space-y-2">
                        <Label>{t('common.workspace_name')}</Label>
                        <Input required value={name} onChange={(e) => setName(e.target.value)} className="h-12" placeholder="Ex. Ministère X, ONG Y, Société Z" data-testid="onboarding-name" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                            <Label>{t('common.industry')}</Label>
                            <select value={industry} onChange={(e) => setIndustry(e.target.value)}
                                className="w-full h-12 px-3 rounded-md border border-input bg-background text-sm"
                                data-testid="onboarding-industry">
                                {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <Label>{t('common.country')}</Label>
                            <Input value={country} onChange={(e) => setCountry(e.target.value)} className="h-12" placeholder="Sénégal, Côte d’Ivoire…" data-testid="onboarding-country" />
                        </div>
                    </div>
                    {error && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">{error}</div>}
                    <Button type="submit" disabled={submitting} className="w-full h-12 bg-primary hover:bg-primary-hover text-primary-foreground" data-testid="onboarding-submit-step0">
                        {submitting ? <Loader2 className="animate-spin" size={16} /> : <>Créer mon espace <ArrowRight size={14} className="ml-2" /></>}
                    </Button>
                </form>
            )}

            {/* ─── STEP 1 : Seed sector project ─── */}
            {step === 1 && ws && (
                <div className="space-y-6">
                    <div className="card-soft p-7" data-testid="onboarding-step1">
                        <div className="flex items-start gap-4 mb-5">
                            <div className="h-12 w-12 rounded-lg bg-primary-light text-primary flex items-center justify-center shrink-0">
                                <FolderPlus size={22} />
                            </div>
                            <div>
                                <h3 className="font-heading text-lg mb-1">Démarrez avec un projet modèle</h3>
                                <p className="text-sm text-foreground/65 leading-relaxed">
                                    Selon votre secteur <strong>{ws.industry || industry}</strong>, nous pouvons pré-créer un projet exemplaire
                                    (phases, jalons, structure budgétaire) que vous personnaliserez ensuite. Idéal pour démarrer en 30 secondes.
                                </p>
                            </div>
                        </div>
                        {seededProject ? (
                            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3">
                                <CheckCircle2 className="text-emerald-600 shrink-0" size={20} />
                                <div className="text-sm">
                                    Projet <strong>{seededProject.name}</strong> créé. Vous le retrouverez dans votre dashboard.
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col sm:flex-row gap-3">
                                <Button onClick={seedProject} disabled={submitting} className="bg-primary hover:bg-primary-hover" data-testid="onboarding-seed-project">
                                    {submitting ? <Loader2 size={14} className="animate-spin" /> : <><Sparkles size={14} className="mr-1.5" /> Créer le projet modèle</>}
                                </Button>
                                <Button variant="outline" onClick={skipStep2} disabled={submitting} data-testid="onboarding-skip-project">
                                    Passer cette étape
                                </Button>
                            </div>
                        )}
                    </div>
                    {seededProject && (
                        <div className="flex justify-end">
                            <Button onClick={() => setStep(2)} className="bg-primary hover:bg-primary-hover">
                                Continuer <ArrowRight size={14} className="ml-2" />
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* ─── STEP 2 : Invite teammate ─── */}
            {step === 2 && ws && (
                <div className="card-soft p-7 space-y-5" data-testid="onboarding-step2">
                    <div className="flex items-start gap-4">
                        <div className="h-12 w-12 rounded-lg bg-primary-light text-primary flex items-center justify-center shrink-0">
                            <UserPlus size={22} />
                        </div>
                        <div>
                            <h3 className="font-heading text-lg mb-1">Invitez votre premier collaborateur</h3>
                            <p className="text-sm text-foreground/65 leading-relaxed">
                                Les projets bailleurs vivent à plusieurs : directeur, comptable, suiveur de chantier.
                                Invitez quelqu'un dès maintenant pour partager le pilotage.
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
                        <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder="collaborateur@organisation.com" type="email" className="h-12"
                            data-testid="onboarding-invite-email" />
                        <Button onClick={sendInvite} disabled={submitting || !inviteEmail.includes('@')}
                            className="bg-primary hover:bg-primary-hover h-12 px-6" data-testid="onboarding-send-invite">
                            {submitting ? <Loader2 size={14} className="animate-spin" /> : 'Envoyer l\'invitation'}
                        </Button>
                    </div>
                    <div className="flex justify-between pt-3 border-t border-border">
                        <Button variant="ghost" onClick={() => setStep(1)}><ArrowLeft size={14} className="mr-1.5" /> Précédent</Button>
                        <Button variant="outline" onClick={skipStep3} disabled={submitting} data-testid="onboarding-skip-invite">
                            Passer cette étape <ArrowRight size={14} className="ml-2" />
                        </Button>
                    </div>
                </div>
            )}

            {/* ─── STEP 3 : Discover Campus 360° ─── */}
            {step === 3 && ws && (
                <div className="card-soft p-7 space-y-5 text-center" data-testid="onboarding-step3">
                    <div className="h-16 w-16 rounded-full bg-primary-light text-primary mx-auto flex items-center justify-center">
                        <GraduationCap size={32} />
                    </div>
                    <h3 className="font-heading text-2xl">Votre espace est prêt 🎉</h3>
                    <p className="text-foreground/65 max-w-md mx-auto leading-relaxed">
                        Découvrez <strong>Campus 360°</strong>, notre académie panafricaine du pilotage projet :
                        10 piliers stratégiques, 14 cas d'usage sectoriels, certification PDF avec QR code de vérification.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                        <Link to="/app/campus" onClick={finish}>
                            <Button className="bg-primary hover:bg-primary-hover h-12 px-6" data-testid="onboarding-cta-campus">
                                <Sparkles size={14} className="mr-2" /> Découvrir Campus 360°
                            </Button>
                        </Link>
                        <Button variant="outline" onClick={finish} className="h-12 px-6" data-testid="onboarding-finish">
                            Aller au tableau de bord <ArrowRight size={14} className="ml-2" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
